import type { SVGProps } from 'react';

/**
 * Local inline icon set.
 *
 * Bundled as JSX rather than pulled from an icon CDN — the spec requires zero
 * external round-trips on cold start. `currentColor` everywhere, so icons theme
 * themselves along with their container.
 */

export type IconName =
  | 'inbox'
  | 'send'
  | 'draft'
  | 'archive'
  | 'trash'
  | 'junk'
  | 'folder'
  | 'star'
  | 'star-filled'
  | 'chevron-right'
  | 'chevron-left'
  | 'chevron-down'
  | 'search'
  | 'compose'
  | 'reply'
  | 'reply-all'
  | 'forward'
  | 'attachment'
  | 'calendar'
  | 'contacts'
  | 'settings'
  | 'menu'
  | 'close'
  | 'refresh'
  | 'offline'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'more'
  | 'check'
  | 'plus'
  | 'mail-unread'
  | 'keyboard'
  | 'shield'
  | 'sparkle'
  | 'tasks'
  | 'grip'
  | 'clock'
  | 'bell'
  | 'bell-off'
  | 'label'
  | 'cloud'
  | 'eye'
  | 'eye-off'
  | 'chevron-up'
  | 'move-top'
  | 'download'
  | 'alert'
  | 'info'
  | 'device'
  | 'key';

/** A warning triangle: shared by Junk and by inline alerts. */
const TRIANGLE = 'M12 3l9 16H3zM12 9v5M12 17h.01';

const PATHS: Record<IconName, string> = {
  inbox: 'M3 12h4l2 3h6l2-3h4M3 12l2.4-6.2A2 2 0 0 1 7.3 4.5h9.4a2 2 0 0 1 1.9 1.3L21 12v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  send: 'M21 3 3 10.5l7 2.5 2.5 7z M21 3l-11 10',
  draft: 'M5 3h9l5 5v13H5zM14 3v5h5M8 13h8M8 17h5',
  archive: 'M3 7h18v3H3zM5 10v10h14V10M10 14h4',
  trash: 'M4 6h16M9 6V4h6v2M6 6l1 15h10l1-15M10 10v7M14 10v7',
  junk: TRIANGLE,
  folder: 'M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  star: 'M12 3.5l2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 10l6.1-.9z',
  'star-filled': 'M12 3.5l2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 10l6.1-.9z',
  'chevron-right': 'M9 5l7 7-7 7',
  'chevron-left': 'M15 5l-7 7 7 7',
  'chevron-down': 'M5 9l7 7 7-7',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-4.2-4.2',
  compose: 'M4 20h16M6 16.5 16.8 5.7a2 2 0 0 1 2.8 2.8L8.8 19.3l-4 1z',
  reply: 'M9 7 3 12l6 5M3 12h9a8 8 0 0 1 8 8',
  'reply-all': 'M8 7 2 12l6 5M13 7l-6 5 6 5M7 12h8a6 6 0 0 1 6 6',
  forward: 'M15 7l6 5-6 5M21 12h-9a8 8 0 0 0-8 8',
  attachment: 'M20 11.5 12.2 19a5 5 0 0 1-7-7l8-8a3.4 3.4 0 1 1 4.8 4.8l-8 8a1.8 1.8 0 1 1-2.5-2.5l7.3-7.3',
  calendar: 'M4 6h16v15H4zM4 10h16M8 3v4M16 3v4',
  contacts: 'M4 5h16v14H4zM12 11a2.2 2.2 0 1 0 0-4.4A2.2 2.2 0 0 0 12 11zM8 16.5c.7-1.6 2.2-2.4 4-2.4s3.3.8 4 2.4',
  settings:
    'M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4zM19.4 13a7.6 7.6 0 0 0 0-2l2-1.5-2-3.4-2.3 1a7.7 7.7 0 0 0-1.7-1l-.3-2.6h-4l-.3 2.6c-.6.2-1.2.6-1.7 1l-2.3-1-2 3.4L6.6 11a7.6 7.6 0 0 0 0 2l-2 1.5 2 3.4 2.3-1c.5.4 1.1.8 1.7 1l.3 2.6h4l.3-2.6c.6-.2 1.2-.6 1.7-1l2.3 1 2-3.4z',
  menu: 'M4 7h16M4 12h16M4 17h16',
  shield: 'M12 3 4.5 6v5.5c0 4.6 3.2 8.2 7.5 9.5 4.3-1.3 7.5-4.9 7.5-9.5V6z',
  close: 'M6 6l12 12M18 6L6 18',
  refresh: 'M20 12a8 8 0 1 1-2.6-5.9M20 4v5h-5',
  offline: 'M3 3l18 18M8.5 16.4a5 5 0 0 1 7 0M5 13a10 10 0 0 1 3.5-2.3M19 13a10 10 0 0 0-8.6-2.8M2 9a15 15 0 0 1 5-3.2M22 9a15 15 0 0 0-8-3.7M12 20h.01',
  sun: 'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  moon: 'M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5z',
  monitor: 'M3 4h18v12H3zM8 20h8M12 16v4',
  more: 'M6 12h.01M12 12h.01M18 12h.01',
  check: 'M4 12.5 9.5 18 20 6.5',
  plus: 'M12 5v14M5 12h14',
  'mail-unread': 'M4 6h16v12H4zM4 7l8 6 8-6',
  keyboard: 'M3 6h18v12H3zM7 10h.01M11 10h.01M15 10h.01M18 10h.01M7 14h10',
  sparkle: 'M12 3.5l1.8 4.9 4.9 1.8-4.9 1.8-1.8 4.9-1.8-4.9-4.9-1.8 4.9-1.8zM18.5 15.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z',
  // A ticked box beside two lines: a checklist, not a single check mark.
  tasks: 'M4 5h5v5H4zM5.3 7.5l1.2 1.2 2.2-2.4M4 14h5v5H4zM12 7.5h8M12 16.5h8',
  // Six dots: the drag handle.
  grip: 'M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  // A bell: a task's reminder.
  label: 'M3.5 12.2V4.5a1 1 0 0 1 1-1h7.7l8.3 8.3a1.4 1.4 0 0 1 0 2l-6.6 6.6a1.4 1.4 0 0 1-2 0zM8 8h.01',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15zM10 20.5a2 2 0 0 0 4 0',
  // The bell struck through: Do not disturb is on (#197).
  'bell-off': 'M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15zM10 20.5a2 2 0 0 0 4 0M3 3l18 18',
  // A cloud: a file shared as a Nextcloud link rather than attached.
  cloud: 'M7 18.5h10.5a4 4 0 0 0 .4-8 6 6 0 0 0-11.6 1.6A3.3 3.3 0 0 0 7 18.5z',
  // An open eye: a password field's Show control; struck through once shown.
  eye: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  'eye-off':
    'M3 3l18 18M10.4 5.6A8.6 8.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a16 16 0 0 1-2.9 3.7M6.5 6.9C4 8.6 2.5 12 2.5 12S6 18.5 12 18.5a8.9 8.9 0 0 0 4.6-1.3M9.9 9.9a3 3 0 0 0 4.2 4.2',
  // The rest came from the Settings routes' private set (#153).
  'chevron-up': 'M5 15l7-7 7 7',
  // First position in a list: a bar with an arrow pointing into it.
  'move-top': 'M4.5 4.5h15M12 19.5V9M7.1 13.9 12 9l4.9 4.9',
  download: 'M12 3v12M7.1 10.5l4.9 4.9 4.9-4.9M4.1 18.75v1.9h15.8v-1.9',
  alert: TRIANGLE,
  info: 'M12 21.4a9.4 9.4 0 1 0 0-18.8 9.4 9.4 0 0 0 0 18.8zM12 10.9v6M12 7.1h.01',
  // A laptop: a signed-in browser or a push-subscribed device.
  device: 'M4.1 4.9h15.8a1.5 1.5 0 0 1 1.5 1.5v8.2a1.5 1.5 0 0 1-1.5 1.5H4.1a1.5 1.5 0 0 1-1.5-1.5V6.4a1.5 1.5 0 0 1 1.5-1.5zM1.5 19.1h21',
  key: 'M7.9 19.9a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5zM10.5 13.5l9.4-9.4M16.5 7.5l2.25 2.25M13.9 10.1l2.25 2.25',
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
  /** Solid icons (star-filled) fill instead of stroke. */
  filled?: boolean;
}

/**
 * Icons drawn as dots (zero-length round-capped strokes) are only as big as
 * the stroke is wide; at the shared 1.6 the ⋯ all but vanished.
 */
const HEAVY: ReadonlySet<IconName> = new Set<IconName>(['more', 'grip']);

export function Icon({ name, size = 18, filled, ...rest }: IconProps) {
  const solid = filled ?? name.endsWith('-filled');
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={solid ? 'currentColor' : 'none'}
      stroke={solid ? 'none' : 'currentColor'}
      strokeWidth={HEAVY.has(name) ? 2.6 : 1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
